<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ReportsAPI from 'dashboard/api/reports';
import { pickScopeFilters } from 'dashboard/api/buildSummaryReportParams';
import { clampDataBetweenTimeline } from 'shared/helpers/ReportsDataHelper';
import { aggregateHourlyToWeekdayHour } from '../helpers/weekdayHeatmapHelper';
import ChatsWeekdayHeatmap from './ChatsWeekdayHeatmap.vue';

const props = defineProps({
  filters: {
    type: Object,
    default: () => ({}),
  },
  archiveContext: {
    type: Object,
    default: () => ({}),
  },
  entityType: {
    type: String,
    default: 'account',
  },
  entityId: {
    type: [String, Number],
    default: null,
  },
});

const { t } = useI18n();
const loading = ref(false);
const weekdayPoints = ref([]);

let lastHeatmapKey = '';

const fetchHeatmap = async () => {
  if (!props.filters.from || !props.filters.to) return;
  if (props.entityType !== 'account' && !props.entityId) return;

  const heatmapKey = JSON.stringify({
    from: props.filters.from,
    to: props.filters.to,
    entityType: props.entityType,
    entityId: props.entityId,
    ...pickScopeFilters(props.filters),
    businessHours: props.filters.businessHours,
  });
  if (heatmapKey === lastHeatmapKey) return;
  lastHeatmapKey = heatmapKey;

  loading.value = true;
  try {
    const scopeParams = pickScopeFilters(props.filters);
    const { data } = await ReportsAPI.getReports({
      metric: 'started_cycles_count',
      from: props.filters.from,
      to: props.filters.to,
      type: props.entityType,
      id: props.entityType === 'account' ? undefined : props.entityId,
      groupBy: 'hour',
      businessHours: props.filters.businessHours,
      timezoneOffset: 0,
      ...scopeParams,
    });

    const clamped = clampDataBetweenTimeline(
      data || [],
      props.filters.from,
      props.filters.to
    );
    weekdayPoints.value = aggregateHourlyToWeekdayHour(clamped);
  } catch {
    weekdayPoints.value = [];
    lastHeatmapKey = '';
  } finally {
    loading.value = false;
  }
};

watch(() => [props.filters, props.entityType, props.entityId], fetchHeatmap, {
  deep: true,
  immediate: true,
});

const heatmapTitle = computed(() =>
  t('SUMMARY_REPORTS.HEATMAPS.STARTED_CYCLES')
);

const archiveContext = computed(() => ({
  reportType: props.entityType === 'account' ? 'account' : props.entityType,
  ...props.archiveContext,
}));

const archiveMetricKey = computed(() =>
  ['agent', 'team'].includes(props.entityType)
    ? 'assignmentEpisodesCount'
    : 'conversationsCount'
);
</script>

<template>
  <div class="mt-4">
    <ChatsWeekdayHeatmap
      :points="weekdayPoints"
      :loading="loading"
      :archive-context="archiveContext"
      :title="heatmapTitle"
      :metric-key="archiveMetricKey"
    />
  </div>
</template>
